import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, Bug, Trash2, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const fmt = (iso) => {
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

export default function AdminErrors() {
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState({});

  const load = () =>
    api.get("/api/admin/errors?limit=200")
      .then(({ data }) => { setItems(data.items); setTotal(data.total); })
      .catch(() => setItems([]));

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try { await api.delete(`/api/admin/errors/${id}`); setItems((x) => x.filter((r) => r.id !== id)); setTotal((t) => Math.max(0, t - 1)); }
    catch { toast.error("Could not delete"); }
  };

  const clearAll = async () => {
    if (!window.confirm("Clear all logged errors?")) return;
    try { await api.delete("/api/admin/errors"); setItems([]); setTotal(0); toast.success("Error log cleared"); }
    catch { toast.error("Could not clear"); }
  };

  return (
    <div data-testid="admin-errors-page">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#5ec0ff] font-semibold mb-1">
        <Bug className="w-3.5 h-3.5" /> Crash reports
      </div>
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white">Client errors</h1>
          <p className="text-sm text-slate-400 mt-1">Uncaught UI crashes caught by the app's error boundary — newest first. {total > 0 && <span className="text-slate-500">({total} total)</span>}</p>
        </div>
        {items?.length > 0 && (
          <Button variant="outline" size="sm" onClick={clearAll} data-testid="admin-errors-clear"
            className="bg-transparent border-rose-700/50 text-rose-300 hover:bg-rose-900/30">
            <Trash2 className="w-4 h-4 mr-1.5" /> Clear all
          </Button>
        )}
      </div>

      <div className="mt-6 rounded-xl border border-slate-800 bg-[#0e1726] overflow-hidden">
        {items === null ? (
          <div className="flex items-center justify-center gap-2 text-slate-500 text-sm py-16">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading crash reports…
          </div>
        ) : items.length === 0 ? (
          <div className="text-center text-slate-500 py-16 text-sm" data-testid="admin-errors-empty">No crashes logged. 🎉</div>
        ) : (
          <div className="divide-y divide-slate-800" data-testid="admin-errors-list">
            {items.map((r) => {
              const isOpen = open[r.id];
              return (
                <div key={r.id} data-testid={`error-row-${r.id}`}>
                  <div className="flex items-center gap-3 px-4 py-3">
                    <button onClick={() => setOpen((o) => ({ ...o, [r.id]: !o[r.id] }))}
                      data-testid={`error-toggle-${r.id}`} className="text-slate-500 hover:text-slate-300 shrink-0">
                      {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </button>
                    <span className="text-[11px] text-slate-500 w-28 shrink-0 font-mono-clinical">{fmt(r.ts)}</span>
                    <span className="text-[10px] font-mono px-2 py-0.5 rounded border border-slate-700 bg-slate-800/60 text-sky-300 shrink-0 max-w-[160px] truncate" title={r.route}>{r.route || "—"}</span>
                    <span className="text-sm text-rose-300 truncate flex-1 min-w-0 font-medium" title={r.message}>{r.message || "Unknown error"}</span>
                    <span className="text-[11px] text-slate-500 hidden md:inline truncate max-w-[160px]">{r.user_email || "anonymous"}</span>
                    <button onClick={() => remove(r.id)} data-testid={`error-delete-${r.id}`} className="text-slate-600 hover:text-rose-400 shrink-0">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  {isOpen && (
                    <div className="px-4 pb-4 pl-14 space-y-2" data-testid={`error-detail-${r.id}`}>
                      {r.stack && (
                        <pre className="text-[10.5px] text-slate-400 bg-[#0b1220] border border-slate-800 rounded-md p-3 overflow-x-auto whitespace-pre-wrap">{r.stack}</pre>
                      )}
                      {r.component_stack && (
                        <pre className="text-[10.5px] text-slate-500 bg-[#0b1220] border border-slate-800 rounded-md p-3 overflow-x-auto whitespace-pre-wrap">{r.component_stack}</pre>
                      )}
                      <div className="text-[10.5px] text-slate-600 break-words">{r.user_agent}</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
